// Copyright 2025, University of Maryland, All Rights Reserved
import { GripVertical, Settings, X } from 'lucide-react'
import * as Icons from 'lucide-react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  Tooltip,
  TooltipTrigger,
  DarkTooltipContent,
  TooltipProvider,
} from '@/components/ui'
import { getLayoutComponent } from '../../../core/layout-registry'
import type { BuilderWidget } from '../types'

interface SortableWidgetItemProps {
  widget: BuilderWidget
  tabId: string
  onRemove: () => void
  onConfigure: () => void
}

export const SortableWidgetItem: React.FC<SortableWidgetItemProps> = ({
  widget,
  tabId,
  onRemove,
  onConfigure,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: widget.id,
    data: {
      type: 'builder-widget',
      widget,
      tabId,
    },
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const layoutComponent = getLayoutComponent(widget.component)
  const displayLabel = widget.label ?? layoutComponent?.display.label ?? widget.component
  const iconName = layoutComponent?.display.icon
  const IconComponent = iconName
    ? (Icons as unknown as Record<string, React.ComponentType<{ className?: string }>>)[iconName]
    : undefined

  return (
    <TooltipProvider delayDuration={300}>
      <div
        ref={setNodeRef}
        style={style}
        className="group flex items-center gap-3 rounded-lg border border-[#e7ecf2] bg-white px-3.5 py-3 transition-all duration-150 hover:border-slate-300 hover:shadow-[0_1px_3px_rgba(2,8,23,0.06)]"
      >
        <Tooltip>
          <TooltipTrigger asChild>
            {/* touch-none: the drag handle owns touch gestures so reordering
                works on mobile instead of scrolling the page */}
            <button
              className="-ml-1 flex h-8 w-5 touch-none cursor-grab items-center justify-center rounded text-slate-300 transition-colors duration-150 hover:text-slate-500 active:cursor-grabbing"
              {...attributes}
              {...listeners}
            >
              <GripVertical className="h-4 w-4" />
              <span className="sr-only">Drag to reorder {displayLabel}</span>
            </button>
          </TooltipTrigger>
          <DarkTooltipContent side="left">Drag to reorder</DarkTooltipContent>
        </Tooltip>

        <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-slate-50 text-slate-500">
          {IconComponent
            ? <IconComponent className="h-4 w-4" />
            : <span className="h-4 w-4 rounded bg-slate-200" />}
        </span>

        <div className="min-w-0 flex-1">
          <p className="truncate text-[13.5px] font-[550] text-slate-800">{displayLabel}</p>
          <p className="truncate font-mono text-[11.5px] text-slate-400">
            {widget.component}
            {widget.instanceId && <span className="text-slate-300"> · {widget.instanceId}</span>}
          </p>
        </div>

        <div className="flex items-center gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[13px] font-medium text-slate-500 transition-colors duration-150 hover:bg-slate-50 hover:text-slate-800"
                onClick={onConfigure}
                data-testid={`builder-widget-settings-${widget.id}`}
              >
                <Settings className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Settings</span>
              </button>
            </TooltipTrigger>
            <DarkTooltipContent>Configure widget</DarkTooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[13px] font-medium text-slate-400 transition-colors duration-150 hover:bg-red-50 hover:text-red-600"
                onClick={onRemove}
                data-testid={`builder-widget-remove-${widget.id}`}
              >
                <X className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Remove</span>
              </button>
            </TooltipTrigger>
            <DarkTooltipContent>Remove from tab</DarkTooltipContent>
          </Tooltip>
        </div>
      </div>
    </TooltipProvider>
  )
}
